import { DEFAULT_LIST_PAGE_SIZE } from '../constants/pagination';

export type ListFilterSpec<F> = {
  strings?: (keyof F & string)[];
  numbers?: (keyof F & string)[];
  booleans?: (keyof F & string)[];
};

export type ListQueryState<F> = {
  page: number;
  perPage: number;
  filters: F;
};

function specKeys<F>(spec: ListFilterSpec<F>): string[] {
  return [...(spec.strings || []), ...(spec.numbers || []), ...(spec.booleans || [])];
}

function readPositiveInt(value: string | null, fallback: number): number {
  const n = parseInt(value || '', 10);
  return Number.isFinite(n) && n >= 1 ? n : fallback;
}

function asRecord(filters: object): Record<string, unknown> {
  return filters as Record<string, unknown>;
}

export function parseListQuery<F>(
  search: string,
  spec: ListFilterSpec<F>,
  defaultPerPage: number = DEFAULT_LIST_PAGE_SIZE
): ListQueryState<F> {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const filters = {} as F;
  const rec = asRecord(filters as object);

  for (const key of spec.strings || []) {
    const v = params.get(key);
    if (v) {
      rec[key] = v;
    }
  }
  for (const key of spec.numbers || []) {
    const v = params.get(key);
    if (v != null && v !== '') {
      const n = Number(v);
      if (Number.isFinite(n)) {
        rec[key] = n;
      }
    }
  }
  for (const key of spec.booleans || []) {
    const v = params.get(key);
    if (v === '1' || v === 'true') {
      rec[key] = true;
    }
  }

  return {
    page: readPositiveInt(params.get('page'), 1),
    perPage: readPositiveInt(params.get('perPage'), defaultPerPage),
    filters,
  };
}

export function buildListQuery<F>(
  prev: URLSearchParams,
  state: ListQueryState<F>,
  spec: ListFilterSpec<F>,
  defaultPerPage: number = DEFAULT_LIST_PAGE_SIZE
): URLSearchParams {
  const next = new URLSearchParams(prev);
  next.delete('page');
  next.delete('perPage');
  for (const key of specKeys(spec)) {
    next.delete(key);
  }

  if (state.page > 1) {
    next.set('page', String(state.page));
  }
  if (state.perPage !== defaultPerPage) {
    next.set('perPage', String(state.perPage));
  }

  const rec = asRecord(state.filters as object);
  for (const key of spec.strings || []) {
    const v = rec[key];
    if (typeof v === 'string' && v !== '') {
      next.set(key, v);
    }
  }
  for (const key of spec.numbers || []) {
    const v = rec[key];
    if (typeof v === 'number' && Number.isFinite(v)) {
      next.set(key, String(v));
    }
  }
  for (const key of spec.booleans || []) {
    if (rec[key] === true) {
      next.set(key, '1');
    }
  }

  return next;
}

export function listQueryEqual(a: URLSearchParams, b: URLSearchParams): boolean {
  const keys: string[] = [];
  a.forEach((_value, key) => {
    if (keys.indexOf(key) === -1) keys.push(key);
  });
  b.forEach((_value, key) => {
    if (keys.indexOf(key) === -1) keys.push(key);
  });
  for (let i = 0; i < keys.length; i++) {
    if (a.get(keys[i]) !== b.get(keys[i])) return false;
  }
  return true;
}
